import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowUpRight,
  Circle,
  Cursor,
  Eraser,
  PencilSimple,
  Rectangle,
  Minus,
  TextT,
  Trash,
} from '@phosphor-icons/react';
import { useDrawingStore } from '../../stores/drawingStore';
import { useSceneStore } from '../../stores/sceneStore';
import { usePlaybackStore } from '../../stores/playbackStore';
import { Tooltip } from '../ui/tooltip';
import type { DrawToolType } from '@spine-workspace/shared';

const COLORS = [
  { label: 'Red',    value: '#ef4444' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Green',  value: '#22c55e' },
  { label: 'Blue',   value: '#3b82f6' },
  { label: 'White',  value: '#ffffff' },
  { label: 'Black',  value: '#1a1a1a' },
];

const SIZES = [2, 4, 8, 16];

const DRAW_TOOLS: { tool: DrawToolType; label: string; icon: React.ReactNode }[] = [
  { tool: 'select',  label: 'Select',  icon: <Cursor size={13} /> },
  { tool: 'pen',     label: 'Pen',     icon: <PencilSimple size={13} /> },
  { tool: 'line',    label: 'Line',    icon: <Minus size={13} /> },
  { tool: 'arrow',   label: 'Arrow',   icon: <ArrowUpRight size={13} /> },
  { tool: 'rect',    label: 'Rect',    icon: <Rectangle size={13} /> },
  { tool: 'ellipse', label: 'Ellipse', icon: <Circle size={13} /> },
  { tool: 'text',    label: 'Text',    icon: <TextT size={13} /> },
];

export default function DrawingToolbar() {
  const {
    brushColor, brushSize, isEraser, filled,
    activeDrawTool,
    setBrushColor, setBrushSize,
    setActiveDrawTool, toggleEraser, toggleFilled,
    undoLastStroke, redoLastStroke, clearFrame,
    redoStacks,
  } = useDrawingStore();
  const sceneId = useSceneStore((s) => s.sceneId);
  const currentFrame = usePlaybackStore((s) => s.currentFrame);

  const showFill = activeDrawTool === 'rect' || activeDrawTool === 'ellipse';
  const canRedo = (redoStacks[currentFrame]?.length ?? 0) > 0;

  function handleUndo() {
    if (sceneId) undoLastStroke(sceneId, currentFrame);
  }

  function handleRedo() {
    if (sceneId) redoLastStroke(sceneId, currentFrame);
  }

  function handleClear() {
    if (sceneId) clearFrame(sceneId, currentFrame);
  }

  return (
    <div className="flex flex-col gap-1 bg-bg-surface/90 backdrop-blur-sm border border-border rounded-lg px-2 py-1.5 shadow-sm">
      {/* Tool selector */}
      <div className="flex items-center gap-0.5">
        {DRAW_TOOLS.map(({ tool, label, icon }) => (
          <Tooltip key={tool} content={label}>
            <button
              onClick={() => setActiveDrawTool(tool)}
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                activeDrawTool === tool && !isEraser
                  ? 'bg-accent-muted text-accent border border-accent/30'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              {icon}
            </button>
          </Tooltip>
        ))}
      </div>

      <div className="h-px bg-border" />

      {/* Style row */}
      <div className="flex items-center gap-1.5">
        {/* Color swatches */}
        <div className="flex items-center gap-1">
          {COLORS.map(({ label, value }) => (
            <Tooltip key={value} content={label}>
              <button
                onClick={() => setBrushColor(value)}
                className={`w-4 h-4 rounded-full border-2 transition-all ${
                  brushColor === value && !isEraser
                    ? 'border-white scale-110 shadow-[0_0_0_1px_rgba(255,255,255,0.3)]'
                    : 'border-transparent hover:scale-110'
                }`}
                style={{ backgroundColor: value }}
              />
            </Tooltip>
          ))}
        </div>

        <div className="w-px h-4 bg-border" />

        {/* Brush size */}
        <div className="flex items-center gap-0.5">
          {SIZES.map((s) => (
            <Tooltip key={s} content={`${s}px`}>
              <button
                onClick={() => setBrushSize(s)}
                className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${
                  brushSize === s && !isEraser
                    ? 'bg-accent-muted text-accent border border-accent/30'
                    : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                <span
                  className="rounded-full bg-current"
                  style={{ width: Math.max(2, s / 2), height: Math.max(2, s / 2) }}
                />
              </button>
            </Tooltip>
          ))}
        </div>

        {showFill && (
          <>
            <div className="w-px h-4 bg-border" />
            <Tooltip content={filled ? 'Filled' : 'Outline only'}>
              <button
                onClick={toggleFilled}
                className={`px-2 h-6 flex items-center justify-center rounded text-xs font-medium transition-colors ${
                  filled ? 'bg-accent-muted text-accent border border-accent/30' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                Fill
              </button>
            </Tooltip>
          </>
        )}
      </div>

      <div className="h-px bg-border" />

      {/* Actions row */}
      <div className="flex items-center gap-0.5">
        <Tooltip content={isEraser ? 'Eraser active' : 'Eraser'}>
          <button
            onClick={toggleEraser}
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
              isEraser ? 'bg-accent-muted text-accent border border-accent/30' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            <Eraser size={13} />
          </button>
        </Tooltip>

        <Tooltip content="Undo last stroke (Cmd+Z)">
          <button
            onClick={handleUndo}
            className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <ArrowCounterClockwise size={13} />
          </button>
        </Tooltip>

        <Tooltip content="Redo last stroke (Cmd+Y)">
          <button
            onClick={handleRedo}
            disabled={!canRedo}
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
              canRedo ? 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover' : 'text-text-disabled opacity-40 cursor-not-allowed'
            }`}
          >
            <ArrowClockwise size={13} />
          </button>
        </Tooltip>

        <Tooltip content="Clear all strokes on this frame">
          <button
            onClick={handleClear}
            className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-colors"
          >
            <Trash size={13} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
